import React, { useRef, useEffect, useState } from 'react';
import { PaperAirplaneIcon, XMarkIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/solid';

interface AudioPreviewProps {
    audioUrl: string;
    audioBlob: Blob;
    onSend: () => void;
    onCancel: () => void;
}

const AudioPreview: React.FC<AudioPreviewProps> = ({ audioUrl, audioBlob, onSend, onCancel }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState<number>(0);

    useEffect(() => {
        setDuration(audioRef.current?.duration || null); // Set duration when audio is loaded
    }, [audioRef.current?.duration]); // Ensure audioBlob is not empty

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            // Get duration once metadata is loaded
            const handleLoadedMetadata = () => {
                const getDuration = function (event: Event) {
                    if (event.target instanceof HTMLAudioElement) {
                        event.target.currentTime = 0;
                        event.target.removeEventListener('timeupdate', getDuration);
                    }
                };

                if (audio.duration === Infinity || isNaN(Number(audio.duration))) {
                    audio.currentTime = 1e101
                    audio.addEventListener('timeupdate', getDuration)
                }
            };
            // Update current time during playback
            const handleTimeUpdate = () => {
                setCurrentTime(audio.currentTime);
            };
            // Reset playback state when audio ends
            const handleEnded = () => {
                setIsPlaying(false);
                setCurrentTime(0); // Reset to beginning
            };

            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
            audio.addEventListener('timeupdate', handleTimeUpdate);
            audio.addEventListener('ended', handleEnded);

            // Preload metadata
            audio.load();

            return () => {
                audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                audio.removeEventListener('timeupdate', handleTimeUpdate);
                audio.removeEventListener('ended', handleEnded);
            };
        }
    }, [audioUrl]); // Re-run effect if audioUrl changes

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (audio) {
            if (isPlaying) {
                audio.pause();
            } else {
                audio.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <div className="p-2 bg-gray-100 dark:bg-gray-800 border-t border-gray-300 dark:border-gray-700 flex items-center space-x-3">
            {/* Cancel Button */}
            <button
                onClick={onCancel}
                className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 focus:outline-none"
                aria-label="Отменить голосовое сообщение"
            >
                <XMarkIcon className="h-6 w-6" />
            </button>

            {/* Audio Player */}
            <div className="flex-grow flex items-center space-x-2 bg-white dark:bg-gray-700 p-2 rounded-md">
                <button
                    onClick={togglePlayPause}
                    className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none"
                    aria-label={isPlaying ? "Пауза" : "Воспроизвести"}
                >
                    {isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
                </button>
                {/* Progress Bar (Simplified) */}
                <div className="flex-grow h-2 bg-gray-300 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-blue-500"
                        style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                    ></div>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 min-w-[40px] text-right">
                    {duration !== null ? formatTime(currentTime) : '--:--'} / {duration !== null ? formatTime(duration) : '--:--'}
                </span>
                <audio ref={audioRef} src={audioUrl} preload="metadata" style={{ display: 'none' }}></audio>
            </div>

            {/* Send Button */}
            <button
                onClick={onSend}
                className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none"
                aria-label="Отправить голосовое сообщение"
            >
                <PaperAirplaneIcon className="h-6 w-6" />
            </button>
        </div>
    );
};

export default AudioPreview;
